import { Extension, Mark, Node, type AnyExtension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'

// Most posts were imported from WordPress, so their HTML carries markup the
// basic editor doesn't model (column layouts, figures with captions, <mark>
// highlights, coloured spans, H4-H6, embeds). Everything below exists so that
// opening and saving an old post keeps that markup instead of silently
// dropping it.

type Attrs = Record<string, string>

function allAttributes(element: HTMLElement): Attrs {
  const attrs: Attrs = {}
  for (const attr of Array.from(element.attributes)) attrs[attr.name] = attr.value
  return attrs
}

// Keeps every HTML attribute of the element as one opaque object.
const htmlAttrs = {
  htmlAttrs: {
    default: {},
    parseHTML: (element: HTMLElement) => allAttributes(element),
    renderHTML: (attributes: { htmlAttrs?: Attrs }) => attributes.htmlAttrs || {},
  },
}

// text-align is owned by the TextAlign extension, so it is stripped here to
// avoid two competing declarations once the editor changes the alignment.
function styleWithoutAlign(element: HTMLElement) {
  const style = (element.getAttribute('style') || '')
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part && !/^text-align\s*:/i.test(part))
    .join('; ')
  return style || null
}

const PreserveAttributes = Extension.create({
  name: 'preserveAttributes',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'blockquote', 'bulletList', 'orderedList', 'listItem', 'horizontalRule', 'codeBlock'],
        attributes: {
          class: {
            default: null,
            parseHTML: (element) => element.getAttribute('class'),
            renderHTML: (attributes) => (attributes.class ? { class: attributes.class } : {}),
          },
          style: {
            default: null,
            parseHTML: (element) => styleWithoutAlign(element),
            renderHTML: (attributes) => (attributes.style ? { style: attributes.style } : {}),
          },
          id: {
            default: null,
            parseHTML: (element) => element.getAttribute('id'),
            renderHTML: (attributes) => (attributes.id ? { id: attributes.id } : {}),
          },
        },
      },
    ]
  },
})

// <div> wrappers (WordPress columns and groups).
const Div = Node.create({
  name: 'div',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes: () => htmlAttrs,
  parseHTML: () => [{ tag: 'div' }],
  renderHTML: ({ HTMLAttributes }) => ['div', HTMLAttributes, 0],
})

const Figure = Node.create({
  name: 'figure',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes: () => htmlAttrs,
  parseHTML: () => [{ tag: 'figure' }],
  renderHTML: ({ HTMLAttributes }) => ['figure', HTMLAttributes, 0],
})

const Figcaption = Node.create({
  name: 'figcaption',
  group: 'block',
  content: 'inline*',
  addAttributes: () => htmlAttrs,
  parseHTML: () => [{ tag: 'figcaption' }],
  renderHTML: ({ HTMLAttributes }) => ['figcaption', HTMLAttributes, 0],
})

// Social embeds, iframes and inline SVG are kept byte-for-byte and shown as a
// placeholder box in the editor.
const EMBED_SELECTORS = [
  'blockquote.instagram-media',
  'blockquote.tiktok-embed',
  'blockquote.twitter-tweet',
  'iframe',
  'video',
  'audio',
  'svg',
  'script',
  'table',
]

const Embed = Node.create({
  name: 'embed',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes: () => ({ html: { default: '' } }),
  parseHTML: () =>
    EMBED_SELECTORS.map((tag) => ({
      tag,
      priority: 1000,
      getAttrs: (element: HTMLElement) => ({ html: element.outerHTML }),
    })),
  renderHTML: ({ node }) => {
    const holder = document.createElement('div')
    holder.innerHTML = String(node.attrs.html || '')
    return holder.firstElementChild ?? ['div', {}]
  },
  addNodeView: () => ({ node }) => {
    const dom = document.createElement('div')
    dom.className = 'admin-editor-embed'
    dom.contentEditable = 'false'
    const html = String(node.attrs.html || '')
    const kind = /instagram/i.test(html)
      ? 'Instagram embed'
      : /tiktok/i.test(html)
        ? 'TikTok embed'
        : /<iframe/i.test(html)
          ? 'Embedded frame'
          : 'Embedded content'
    dom.textContent = `${kind} (kept as is)`
    return { dom }
  },
})

const Highlight = Mark.create({
  name: 'highlightMark',
  addAttributes: () => htmlAttrs,
  parseHTML: () => [{ tag: 'mark' }],
  renderHTML: ({ HTMLAttributes }) => ['mark', HTMLAttributes, 0],
})

// Only spans that carry styling; bare spans are dropped as before.
const StyledSpan = Mark.create({
  name: 'styledSpan',
  addAttributes: () => htmlAttrs,
  parseHTML: () => [{ tag: 'span[style]' }, { tag: 'span[class]' }],
  renderHTML: ({ HTMLAttributes }) => ['span', HTMLAttributes, 0],
})

const RichImage = Image.extend({
  addAttributes() {
    const keep = ['srcset', 'sizes', 'width', 'height', 'loading', 'decoding', 'class', 'style', 'id', 'data-id', 'title']
    const extra = Object.fromEntries(
      keep.map((name) => [
        name,
        {
          default: null,
          parseHTML: (element: HTMLElement) => element.getAttribute(name),
          renderHTML: (attributes: Record<string, unknown>) =>
            attributes[name] == null ? {} : { [name]: String(attributes[name]) },
        },
      ]),
    )
    return {
      ...this.parent?.(),
      ...extra,
      // An image wrapped in a link (e.g. shop links on product photos). Images
      // are blocks and can't carry the link mark, so the <a> is kept here.
      link: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const parent = element.parentElement
          return parent?.tagName === 'A' && parent.children.length === 1 ? allAttributes(parent) : null
        },
        renderHTML: () => ({}),
      },
    }
  },
  renderHTML({ node, HTMLAttributes }) {
    const img = ['img', HTMLAttributes] as const
    return node.attrs.link ? ['a', node.attrs.link, img] : img
  },
})

export function editorExtensions(placeholder = 'Write the piece…'): AnyExtension[] {
  return [
    StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] }, link: false }),
    RichImage.configure({ inline: false, allowBase64: false }),
    // No default target/rel, so existing links keep exactly what they had.
    Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { target: null, rel: null } }),
    Placeholder.configure({ placeholder }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    PreserveAttributes,
    Div,
    Figure,
    Figcaption,
    Embed,
    Highlight,
    StyledSpan,
  ]
}

// The editor model requires a paragraph inside every list item; unwrap it on
// the way out so <li>text</li> stays <li>text</li>.
export function cleanEditorHtml(html: string) {
  return html.replace(/<li([^>]*)><p>((?:(?!<\/?p[\s>]).)*)<\/p><\/li>/g, '<li$1>$2</li>')
}
